/** The landlord's secure link for a writ of ejectment set-out: see the date, confirm, or ask to cancel. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { fmtWhen, setAgencyTz } from '../shared/setout.tsx';
import { Card, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

export function SetOutLink({ token }: { token: string }) {
  const r = useLoad(() => api.get<any>(`/api/public/setouts/${token}`), [token]);
  const a = useAction();
  const [name, setName] = useState('');
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const d = r.data;
  if (d) setAgencyTz(d.timezone);
  const confirm = () => a.run(async () => (await api.post(`/api/public/setouts/${token}/confirm`, { name: name.trim() }), r.reload()));
  const askCancel = () => a.run(async () => (await api.post(`/api/public/setouts/${token}/cancel-request`, { name: name.trim(), reason: reason.trim() }), setAsking(false), r.reload()));
  return (
    <div className="xfer-page">
      <header className="xfer-top">
        <img src="/civilhound-icon.webp" alt="" width={36} height={36} />
        <div>
          <strong>{d?.agency ?? 'Sheriff’s Office'}</strong>
          <div className="small">Writ of ejectment · set-out</div>
        </div>
      </header>
      <main className="xfer-main stack">
        {r.error ? (
          <ErrorBox error={r.error} />
        ) : !d ? (
          <Spinner />
        ) : (
          <>
            <div className="so-when">
              <div className="small">
                {d.status === 'completed' ? 'Set-out completed' : d.status === 'cancelled' ? 'Set-out cancelled' : 'Set-out scheduled for'}
              </div>
              <div className="so-when-big">{fmtWhen(d.completedAt ?? d.cancelledAt ?? d.scheduledFor)}</div>
              <div>{d.address}</div>
            </div>
            <Card title="Details">
              <dl className="kv">
                <dt>Tenant</dt>
                <dd>{d.tenant}</dd>
                <dt>Case</dt>
                <dd className="mono">{d.caseNumber}</dd>
                <dt>Deputies</dt>
                <dd>{d.deputies}</dd>
                <dt>Questions</dt>
                <dd>
                  {d.agency}
                  {d.agencyPhone ? `, ${d.agencyPhone}` : ''} — reference <span className="mono">{d.paperNumber}</span>
                </dd>
              </dl>
            </Card>
            {d.status === 'scheduled' && (
              <>
                {d.cancelRequestedAt ? (
                  <Notice tone="warn">You asked to cancel ({d.cancelReason}). The sheriff’s office will call you; until then the set-out stays on the schedule.</Notice>
                ) : d.confirmedAt ? (
                  <Notice tone="ok">✓ Confirmed by {d.confirmedName}. Please be there on time with a way to change the locks.</Notice>
                ) : (
                  <Notice tone="info">Please confirm you (or someone for you) will be there with a way to change the locks. Deputies can’t wait if no one is there for the landlord.</Notice>
                )}
                <Card title={asking ? 'Ask to cancel' : 'Confirm'}>
                  <Field label="Your name">
                    <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                  </Field>
                  {asking && (
                    <Field label="Why? (tenant paid, moved out, case settled…)">
                      <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                  )}
                  <ErrorBox error={a.error} />
                  {asking ? (
                    <div className="row gap wrap">
                      <button className="btn warn big" disabled={a.busy || name.trim().length < 2 || reason.trim().length < 3} onClick={askCancel}>
                        Send cancel request
                      </button>
                      <button className="btn ghost" onClick={() => setAsking(false)}>
                        Back
                      </button>
                    </div>
                  ) : (
                    <div className="row gap wrap">
                      {!d.confirmedAt && (
                        <button className="btn primary big" disabled={a.busy || name.trim().length < 2} onClick={confirm}>
                          I’ll be there
                        </button>
                      )}
                      {!d.cancelRequestedAt && (
                        <button className="btn ghost" onClick={() => setAsking(true)}>
                          Tenant paid or moved out? Ask to cancel
                        </button>
                      )}
                    </div>
                  )}
                  <p className="small muted">A cancel request goes to the sheriff’s office. The set-out isn’t cancelled until they confirm it.</p>
                </Card>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
